import { ConstructionWorkItemDetail } from '@euler/model/ConstructionWorkItemDetail';
import { ConstructionProcedureType } from '@euler/model/PredefinedConstructionWorkItem';
import { IdType } from '@euler/model/common';
import { ProductConstructionProcedureStdImage } from '@euler/model/product';
import { ProductAuthorInfo } from '@euler/model/product/ProductAuthorInfo';
import { TaskProductManifestItemIdentifier } from '@euler/model/task-detail/TaskProductManifest';
import { InspectionTaskJobStatus } from '../enum';
import { Assignee } from './Assignee';
import { ConstructionJobDetail } from './ConstructionJobDetail';
import {
  ConstructionJobMedia,
  ConstructionJobMediaCommitPayload,
  ConstructionJobMediaStaged,
} from './ConstructionJobMedia';

export const kConstructionJobContextVersion = '1.0';

export type ConstructionJobProductProcedureApplyType =
  | 'merge'
  | 'replace'
  | 'ignore';

export type ConstructionJobProductConfirmationProcedureInfo = {
  name: string;
  requiresPhoto?: boolean;
  removable?: boolean;
  stdImages: ProductConstructionProcedureStdImage[] | undefined;
};

export type ConstructionJobProductConfirmationItem = {
  contractId: IdType;
  productId: IdType;
  adopted: boolean;
  qty?: number;
  procedures?: ConstructionJobProductConfirmationProcedureInfo[];
  proceduresRequired?: boolean;
  proceduresAppliedAs?: ConstructionJobProductProcedureApplyType;
  // Closer attribution for the manifest item. When present on a
  // confirm (and `adopted` is `true`), the BE writes it through to the
  // manifest item's closer. NOT persisted in the job-context items.
  closer?: ProductAuthorInfo;
  authorId: number;
  authorName: string;
  confirmedAt: string | Date;
};

export type ConstructionJobProductConfirmationItemPayload = Omit<
  ConstructionJobProductConfirmationItem,
  'authorId' | 'authorName' | 'confirmedAt'
>;

export type ConstructionJobProductConfirmation = {
  items: ConstructionJobProductConfirmationItem[];
};

export type ConstructionJobProductConfirmationResult = {
  context: ConstructionJobContextData;
};

export type ConstructionJobContextProcedureInfo = {
  name: string;
  type: ConstructionProcedureType | undefined;
  removable: boolean | undefined;
  allowChooseMediaFromGallery: boolean | undefined;
};

export type ConstructionJobContextData = {
  version: string;
  category?: string;
  procedures: ConstructionJobContextProcedureInfo[];
  defaultProcedures?: ConstructionJobContextProcedureInfo[];
  resolved?: string[];
  productConfirmation?: ConstructionJobProductConfirmation;
  saved?: boolean;
};

export type ConstructionJobProcedure = {
  name: string;
  type: ConstructionProcedureType;
  medias: ConstructionJobMedia[];
  removable: boolean | undefined;
  allowChooseMediaFromGallery: boolean | undefined;
};

export type ConstructionJob = {
  id: number;
  name: string;
  label?: string;
  status: InspectionTaskJobStatus;
  isCustom: boolean;
  commitId?: string;
  remark?: string;
  technicianId?: number;
  technicianName?: string;
  details: ConstructionJobDetail[];
  procedures: ConstructionJobProcedure[];
  createdAt: string | Date;
  updatedAt?: string | Date;
  finishedAt?: string | Date;
  context?: ConstructionJobContextData;
};

export type ConstructionJobProcedureCommitPayload = {
  name: string;
  type: ConstructionProcedureType;
  medias: ConstructionJobMediaCommitPayload[];
  removable: boolean | undefined;
  allowChooseMediaFromGallery: boolean | undefined;
};

export type ConstructionJobCommitPayload = {
  id: number;
  label?: string;
  commitId?: string;
  technicianId: number;
  technicianName: string;
  category?: string;
  procedures: ConstructionJobProcedureCommitPayload[];
  defaultProcedures?: ConstructionJobContextProcedureInfo[];
  saveOnly?: boolean;
  productRefs?: TaskProductManifestItemIdentifier[];
  remark?: string;
  resolved?: string[];
  nextServiceInfo?: {
    nextServiceMileage: number;
    nextServiceDate: string;
  };
};

export type ScheduleConstructionJobPayload = {
  name: string;
  // if work items are not specified, it will become a custom job.
  workItems?: ConstructionWorkItemDetail[];
  assignee?: Assignee;
  context?: ConstructionJobContextData;
};

//#region staged data models

export type ConstructionJobProcedureStaged = {
  name: string;
  type: ConstructionProcedureType;
  medias: ConstructionJobMediaStaged[];
  removable: boolean | undefined;
  allowChooseMediaFromGallery: boolean | undefined;
  stdImages?: ProductConstructionProcedureStdImage[];
};

export type ConstructionJobContextDataStaged = ConstructionJobContextData;

export type ConstructionJobStaged = {
  id: number;
  name: string;
  commitId?: string;
  remark?: string;
  technicianId: number;
  technicianName: string;
  procedures: ConstructionJobProcedureStaged[];
  context: ConstructionJobContextDataStaged;
};

//#endregion

export type PatchConstructionJobPayload = {
  id: number;
  label?: string;
  commitId?: string;
  technicianId: number;
  technicianName: string;
  category?: string;
  defaultProcedures?: ConstructionJobContextProcedureInfo[];
  finished?: boolean;
  remark?: string;
  resolved?: string[];
  productRefs?: TaskProductManifestItemIdentifier[];
  procedures: ConstructionJobProcedurePatch[];
};

export type ConstructionJobProcedurePatch = { name: string } & (
  | {
      operation: 'add';
      type: ConstructionProcedureType;
      medias: ConstructionJobMediaPatchAdd[];
      removable: boolean | undefined;
      allowChooseMediaFromGallery: boolean | undefined;
      index: number;
    }
  | {
      operation: 'update';
      type: ConstructionProcedureType;
      medias: ConstructionJobMediaPatch[];
      removable: boolean | undefined;
      allowChooseMediaFromGallery: boolean | undefined;
      index: number;
    }
  | {
      operation: 'remove';
    }
);

export type ConstructionJobMediaPatchAdd = {
  operation: 'add';
  media: ConstructionJobMediaCommitPayload;
};

export type ConstructionJobMediaPatchRemove = {
  operation: 'remove';
  id?: number;
  url: string;
};

export type ConstructionJobMediaPatch =
  | ConstructionJobMediaPatchAdd
  | ConstructionJobMediaPatchRemove;
